import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { fmtDateTime } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";

const Ico = ({ name, ...props }) => {
  const C = Icons[name] || Icons.Circle;
  return <C {...props} />;
};

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!q) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`);
        setResults(data);
        setOpen(true);
      } catch (e) {}
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="relative w-full max-w-md hidden sm:block">
      <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
      <Input
        data-testid="global-search-input"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => q && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        placeholder="Search employees, records…"
        className="pl-9 bg-slate-50 border-slate-200 h-9"
      />
      {open && results.length > 0 && (
        <div className="absolute mt-1 w-full bg-white rounded-lg border border-slate-200 shadow-lg z-50 overflow-hidden" data-testid="global-search-results">
          {results.map((r, i) => (
            <button
              key={i}
              onMouseDown={() => { navigate(r.path); setOpen(false); setQ(""); }}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-3 border-b border-slate-100 last:border-0"
            >
              <Badge variant="secondary" className="text-[10px]">{r.type}</Badge>
              <div>
                <div className="text-sm font-medium text-slate-800">{r.title}</div>
                <div className="text-xs text-slate-500">{r.subtitle}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function NotificationBell() {
  const [items, setItems] = useState([]);
  const navigate = useNavigate();
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
    } catch (e) {}
  }, []);
  useEffect(() => { load(); const i = setInterval(load, 30000); return () => clearInterval(i); }, [load]);
  const unread = items.filter((n) => !n.read).length;

  const markAll = async () => { await api.post("/notifications/read-all"); load(); };

  return (
    <Sheet>
      <SheetTrigger asChild>
        <button data-testid="notification-bell" className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors">
          <Icons.Bell className="h-5 w-5 text-slate-600" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-blue-600 text-white text-[10px] flex items-center justify-center font-semibold">
              {unread}
            </span>
          )}
        </button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-md p-0">
        <SheetHeader className="p-4 border-b flex-row items-center justify-between">
          <SheetTitle>Notifications</SheetTitle>
          <button onClick={markAll} className="text-xs text-blue-600 hover:underline" data-testid="notif-mark-all">Mark all read</button>
        </SheetHeader>
        <div className="overflow-y-auto h-[calc(100vh-70px)]">
          {items.length === 0 && <div className="p-8 text-center text-sm text-slate-400">No notifications</div>}
          {items.map((n) => (
            <button
              key={n.id}
              onClick={async () => { await api.post(`/notifications/${n.id}/read`); load(); if (n.link) navigate(n.link); }}
              className={`w-full text-left p-4 border-b border-slate-100 hover:bg-slate-50 flex gap-3 ${n.read ? "opacity-60" : ""}`}
            >
              <div className={`mt-1 h-2 w-2 rounded-full shrink-0 ${n.read ? "bg-slate-300" : "bg-blue-600"}`} />
              <div>
                <div className="text-sm font-medium text-slate-800">{n.title}</div>
                <div className="text-xs text-slate-500">{n.message}</div>
                <div className="text-[11px] text-slate-400 mt-1">{fmtDateTime(n.created_at)}</div>
              </div>
            </button>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

const crumbMap = {
  dashboard: "Dashboard", profile: "My Profile", hris: "HRIS", leave: "Leave",
  payroll: "Payroll", tasks: "Task", notifications: "Notifications", settings: "Master Settings",
  attendance: "Attendance", procurement: "Procurement", assets: "Assets", documents: "Documents",
  esd: "ESD", tenants: "Tenants", itam: "Asset Management", finance: "Finance",
};

const ESD_MGR = ["super_admin", "admin", "supervisor", "approver"];
const ESD_NAV_ROLES = {
  "esd-operators": ESD_MGR,
  "esd-requests": [...ESD_MGR, "requestor"],
  "esd-inspections": ESD_MGR,
  "esd-queues": ESD_MGR,
  "esd-mobilizations": [...ESD_MGR, "operator"],
  "esd-maintenance": ["super_admin", "admin", "supervisor"],
  "esd-utilizations": ["super_admin", "admin", "supervisor"],
  "esd-contracts": ["super_admin", "admin"],
  "esd-billings": ["super_admin", "admin"],
  "esd-settings": ["super_admin", "admin"],
};

export default function AppShell({ children }) {
  const { user, settings, logout } = useAuth();
  const [nav, setNav] = useState([]);
  const [modules, setModules] = useState([]);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState({ "hris-group": true });
  const [pendingCount, setPendingCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const [blocked, setBlocked] = useState([]);
  const [esdRole, setEsdRole] = useState(undefined);

  useEffect(() => {
    api.get("/me/navigation").then(({ data }) => setNav(data)).catch(() => {});
    api.get("/modules").then(({ data }) => setModules(data)).catch(() => {});
    api.get("/modules/blocked-routes").then(({ data }) => setBlocked(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (blocked.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"))) navigate("/tenants", { replace: true });
  }, [blocked, location.pathname, navigate]);

  useEffect(() => {
    const load = () => api.get("/hris/requests/pending-count").then(({ data }) => setPendingCount(data.count || 0)).catch(() => {});
    load();
    const i = setInterval(load, 30000);
    return () => clearInterval(i);
  }, [location.pathname]);

  // When a user is inside a module (launched from Tenants), swap the sidebar to that
  // module's sub-navigation. Derived from the current route (stateless → survives refresh).
  let activeModule = null;
  let bestLen = -1;
  const walkMatch = (items, m) => {
    for (const c of items || []) {
      if (c.children) { walkMatch(c.children, m); continue; }
      if (c.path && (location.pathname === c.path || location.pathname.startsWith(c.path + "/"))) {
        if (c.path.length > bestLen) { bestLen = c.path.length; activeModule = m; }
      }
    }
  };
  for (const m of modules) walkMatch(m.children, m);
  for (const m of modules) {
    if (location.pathname.startsWith(`/module/${m.module_id}/`)) { activeModule = m; }
  }
  const inModule = !!activeModule;
  const inEsd = activeModule?.module_id === "esd";
  const inHris = activeModule?.module_id === "hris";
  useEffect(() => {
    if (inEsd && esdRole === undefined) {
      api.get("/esdmp/auth/me").then(({ data }) => setEsdRole(data.role)).catch(() => setEsdRole(null));
    }
  }, [inEsd, esdRole]);
  const moduleItems = inEsd
    ? (activeModule.children || []).filter((c) => esdRole && (!ESD_NAV_ROLES[c.key] || ESD_NAV_ROLES[c.key].includes(esdRole)))
    : activeModule?.children;
  const activeCompany = (user?.company_ids || []).length;
  const sysName = settings?.short_name || "UEMP";
  const seg = location.pathname.split("/")[1] || "dashboard";

  const SidebarInner = (
    <>
      <div className={`flex items-center gap-2.5 px-4 h-16 border-b border-slate-800 ${collapsed ? "justify-center" : ""}`}>
        <div className="h-9 w-9 rounded-lg bg-blue-600 flex items-center justify-center shrink-0">
          <Icons.Layers className="h-5 w-5 text-white" />
        </div>
        {!collapsed && (
          <div className="leading-tight">
            <div className="text-white font-semibold text-sm">{sysName}</div>
            <div className="text-slate-400 text-[10px] tracking-wide">ENTERPRISE PLATFORM</div>
          </div>
        )}
      </div>
      <nav className="flex-1 min-h-0 overflow-y-auto overscroll-contain py-3 px-2 space-y-0.5" data-testid="sidebar-nav-scroll">
        {inModule && (
          <div className="mb-2">
            <button onClick={() => { setMobileOpen(false); navigate("/tenants"); }} data-testid="module-back-btn"
              className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors ${collapsed ? "justify-center" : ""}`}>
              <Icons.ChevronLeft className="h-[18px] w-[18px] shrink-0" />
              {!collapsed && <span>Back to Tenants</span>}
            </button>
            {!collapsed && (
              <div className="px-3 pt-3 pb-1 text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                <Ico name={activeModule.icon} className="h-3.5 w-3.5" /> {activeModule.name}
              </div>
            )}
            {activeModule.module_id !== "hris" && (activeModule.module_id !== "servicedesk" || (activeModule.children || []).some((c) => c.key === "sd-settings")) && <Link to={`/module/${activeModule.module_id}/settings`} data-testid="module-settings-link"
              onClick={() => setMobileOpen(false)}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${location.pathname.startsWith(`/module/${activeModule.module_id}/settings`) ? "bg-blue-600 text-white font-medium" : "text-slate-300 hover:bg-slate-800 hover:text-white"} ${collapsed ? "justify-center" : ""}`}>
              <Icons.SlidersHorizontal className="h-[16px] w-[16px] shrink-0" />
              {!collapsed && <span>Module Settings</span>}
            </Link>}
          </div>
        )}
        {(inModule ? moduleItems : nav).map((item) => {
          if (item.children) {
            const childActive = item.children.some((c) => location.pathname === c.path || location.pathname.startsWith(c.path + "/"));
            const open = openGroups[item.key] ?? !inHris;
            return (
              <div key={item.key}>
                <button
                  data-testid={`nav-group-${item.key}`}
                  onClick={() => setOpenGroups((g) => ({ ...g, [item.key]: !open }))}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                    childActive ? "text-white font-medium" : "text-slate-300 hover:bg-slate-800 hover:text-white"
                  } ${collapsed ? "justify-center" : ""}`}
                >
                  <Ico name={item.icon} className="h-[18px] w-[18px] shrink-0" />
                  {!collapsed && (
                    <>
                      <span className="flex-1 text-left">{item.label}</span>
                      {item.key === "for-approval" && pendingCount > 0 && (
                        <span data-testid="approval-badge" className="h-5 min-w-5 px-1.5 rounded-full bg-rose-500 text-white text-[10px] flex items-center justify-center font-semibold">{pendingCount}</span>
                      )}
                      <Icons.ChevronDown className={`h-4 w-4 transition-transform ${open ? "" : "-rotate-90"}`} />
                    </>
                  )}
                </button>
                {open && (
                  <div className={collapsed ? "space-y-0.5" : "mt-0.5 ml-[18px] pl-3 border-l border-slate-800 space-y-0.5"}>
                    {item.children.map((c) => {
                      const active = location.pathname === c.path || (c.key !== "sd-dash" && location.pathname.startsWith(c.path + "/"));
                      return (
                        <Link
                          key={c.key}
                          to={c.path}
                          data-testid={`nav-${c.key}`}
                          onClick={() => setMobileOpen(false)}
                          className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                            active ? "bg-blue-600 text-white font-medium" : "text-slate-300 hover:bg-slate-800 hover:text-white"
                          } ${collapsed ? "justify-center" : ""}`}
                        >
                          <Ico name={c.icon} className="h-[16px] w-[16px] shrink-0" />
                          {!collapsed && <span>{c.label}</span>}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }
          const active = location.pathname === item.path || (location.pathname.startsWith(item.path + "/") && (item.path !== "/hris" || /^\/hris\/[0-9a-f-]{36}$/.test(location.pathname)) && item.path !== "/ams" && item.path !== "/servicedesk");
          return (
            <Link
              key={item.key}
              to={item.path}
              data-testid={`nav-${item.key}`}
              onClick={() => setMobileOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                active ? "bg-blue-600 text-white font-medium" : "text-slate-300 hover:bg-slate-800 hover:text-white"
              } ${collapsed ? "justify-center" : ""}`}
            >
              <Ico name={item.icon} className="h-[18px] w-[18px] shrink-0" />
              {!collapsed && <span>{item.label}</span>}
            </Link>
          );
        })}
      </nav>
      <div className="p-3 border-t border-slate-800">
        {!collapsed && (
          <div className="text-[11px] text-slate-500 px-2">
            {settings?.system_name || "Unified Enterprise Management Platform"}
          </div>
        )}
      </div>
    </>
  );

  const initials = (user?.full_name || "U").split(" ").map((p) => p[0]).slice(0, 2).join("");

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Desktop sidebar */}
      <aside className={`hidden lg:flex flex-col bg-slate-900 transition-all duration-200 sticky top-0 h-screen shrink-0 ${collapsed ? "w-[72px]" : "w-[260px]"}`} data-testid="desktop-sidebar">
        {SidebarInner}
      </aside>

      {/* Mobile sidebar */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-[260px] bg-slate-900 flex flex-col">{SidebarInner}</aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="sticky top-0 z-30 h-16 bg-white/90 backdrop-blur-md border-b border-slate-200 flex items-center gap-3 px-4">
          <button className="hidden lg:block p-2 rounded-lg hover:bg-slate-100" onClick={() => setCollapsed(!collapsed)} data-testid="sidebar-toggle">
            <Icons.PanelLeft className="h-5 w-5 text-slate-600" />
          </button>
          <button className="lg:hidden p-2 rounded-lg hover:bg-slate-100" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-toggle">
            <Icons.Menu className="h-5 w-5 text-slate-600" />
          </button>
          <GlobalSearch />
          <div className="flex-1" />
          <Badge variant="outline" className="hidden md:flex gap-1.5 border-slate-200 text-slate-600">
            <Icons.Building2 className="h-3.5 w-3.5" /> {activeCompany} {activeCompany === 1 ? "Company" : "Companies"}
          </Badge>
          <NotificationBell />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 pl-1.5 pr-2 py-1 rounded-lg hover:bg-slate-100" data-testid="user-menu">
                <Avatar className="h-8 w-8">
                  {user?.photo_url && <AvatarImage src={user.photo_url} alt={user?.full_name} className="object-cover" data-testid="user-menu-avatar" />}
                  <AvatarFallback className="bg-blue-600 text-white text-xs font-semibold">{initials}</AvatarFallback>
                </Avatar>
                <div className="hidden md:block text-left leading-tight">
                  <div className="text-sm font-medium text-slate-800">{user?.full_name}</div>
                  <div className="text-[11px] text-slate-500">{user?.role}</div>
                </div>
                <Icons.ChevronDown className="h-4 w-4 text-slate-400 hidden md:block" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="font-medium">{user?.full_name}</div>
                <div className="text-xs text-slate-500 font-normal">{user?.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
                <Icons.User className="h-4 w-4 mr-2" /> My Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/tasks")}>
                <Icons.CheckSquare className="h-4 w-4 mr-2" /> My Tasks
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} data-testid="logout-button" className="text-red-600">
                <Icons.LogOut className="h-4 w-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Breadcrumb */}
        <div className="px-5 sm:px-8 pt-4 flex items-center gap-1.5 text-xs text-slate-500">
          <Icons.Home className="h-3.5 w-3.5" />
          <span>/</span>
          <span className="text-slate-700 font-medium">{crumbMap[seg] || "Dashboard"}</span>
        </div>

        <main className="flex-1 px-5 sm:px-8 py-5">{children}</main>
      </div>
    </div>
  );
}
